export default function AnalyticsWhatIs() {
  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-[#1A2B4C] mb-5 font-['Playfair_Display'] leading-tight">
            Что такое сквозная аналитика и зачем она вам?
          </h2>
          <p className="text-base sm:text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed font-['Montserrat'] text-justify">
            Обычные счетчики (Яндекс.Метрика или Google Analytics) показывают только «верхушку айсберга»: сколько людей зашло на сайт и нажало на кнопки. Но они не знают, что происходит после звонка.
          </p>
        </div>

        {/* Definition block */}
        <div className="bg-[#F8F9FA] rounded-3xl border border-[#C5A880]/20 p-7 sm:p-12 mb-12 sm:mb-16">
          <p className="text-base sm:text-lg text-gray-700 mb-7 font-['Montserrat'] leading-relaxed text-justify">
            <strong className="text-[#1A2B4C]">Сквозная аналитика</strong> — это система, которая соединяет данные из всех источников:
          </p>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: 'ri-advertisement-line', title: 'Реклама', desc: 'Где и сколько вы тратите (Google, Яндекс, соцсети).', color: 'bg-orange-100 text-orange-600' },
              { icon: 'ri-global-line', title: 'Сайт', desc: 'Как ведут себя посетители и какие заявки оставляют.', color: 'bg-teal-100 text-teal-600' },
              { icon: 'ri-customer-service-2-line', title: 'CRM или отдел продаж', desc: 'Кто из этих посетителей реально заплатил деньги.', color: 'bg-[#C5A880]/20 text-[#B39770]' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-[#C5A880]/15">
                <div className={`w-12 h-12 ${item.color} rounded-full flex items-center justify-center mb-4`}>
                  <i className={`${item.icon} text-2xl`}></i>
                </div>
                <h4 className="text-base font-bold text-[#1A2B4C] mb-2 font-['Montserrat']">{item.title}</h4>
                <p className="text-sm text-gray-600 leading-relaxed font-['Montserrat'] text-justify">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-7 bg-[#1A2B4C] rounded-2xl px-6 sm:px-8 py-5 flex items-start gap-4">
            <div className="w-10 h-10 bg-[#C5A880] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-trophy-line text-white text-xl"></i>
            </div>
            <p className="text-sm sm:text-base text-white/90 leading-relaxed font-['Montserrat'] text-justify">
              <strong className="text-[#C5A880]">Результат:</strong> Вы защищены от инвестиций в рекламу, которая приносит только пустые клики. Вы видите реальную окупаемость каждого объявления.
            </p>
          </div>
        </div>

        {/* Chaos vs Order */}
        <div className="mb-4">
          <h3 className="text-xl sm:text-3xl font-bold text-[#1A2B4C] text-center mb-8 font-['Playfair_Display']">
            Хаос vs Порядок
          </h3>
          <div className="grid sm:grid-cols-2 gap-5 sm:gap-8">
            {/* Chaos */}
            <div className="relative rounded-3xl overflow-hidden border-2 border-red-200">
              <div className="absolute inset-0 z-0">
                <img
                  src="https://public.readdy.ai/ai/img_res/6318930e771525f27a2b20ffec64c0f0.jpg"
                  alt="Хаос без аналитики"
                  className="w-full h-full object-cover object-top opacity-30"
                />
              </div>
              <div className="relative z-10 bg-red-50/90 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-line text-white text-xl font-bold"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-red-700 font-['Playfair_Display']">Без аналитики</h4>
                </div>
                <p className="text-sm sm:text-base text-red-800 font-semibold mb-3 font-['Montserrat']">Маркетинг на ощупь</p>
                <p className="text-sm text-gray-700 leading-relaxed font-['Montserrat'] text-justify">
                  Вы видите общие затраты и общую выручку, но не знаете, что происходит внутри. Деньги вливаются в рекламу вслепую, а какой канал работает — загадка.
                </p>
                <div className="mt-5 space-y-2">
                  {['Непонятно, какой канал приносит продажи', 'Бюджет расходуется неэффективно', 'Решения принимаются на интуиции'].map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <i className="ri-close-circle-line text-red-500 text-base flex-shrink-0"></i>
                      <span className="text-sm text-gray-700 font-['Montserrat']">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Order */}
            <div className="relative rounded-3xl overflow-hidden border-2 border-[#C5A880]/40">
              <div className="absolute inset-0 z-0">
                <img
                  src="https://readdy.ai/api/search-image?query=clean%20organized%20modern%20analytics%20dashboard%20on%20large%20monitor%20showing%20colorful%20bar%20charts%20and%20conversion%20funnels%2C%20bright%20professional%20office%20with%20natural%20light%2C%20minimalist%20desk%20setup%2C%20golden%20hour%20warm%20lighting%2C%20sharp%20focus%20with%20beautiful%20bokeh%20background%2C%20premium%20business%20intelligence%20visualization&width=700&height=420&seq=analytics-order&orientation=landscape"
                  alt="Порядок со сквозной аналитикой"
                  className="w-full h-full object-cover object-top opacity-25"
                />
              </div>
              <div className="relative z-10 bg-[#F8F9FA]/95 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-[#C5A880] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-xl font-bold"></i>
                  </div>
                  <h4 className="text-lg sm:text-xl font-bold text-[#1A2B4C] font-['Playfair_Display']">Со сквозной аналитикой</h4>
                </div>
                <p className="text-sm sm:text-base text-[#1A2B4C] font-semibold mb-3 font-['Montserrat']">Оцифрованный бизнес</p>
                <p className="text-sm text-gray-700 leading-relaxed font-['Montserrat'] text-justify">
                  Вы управляете рекламным бюджетом как инвестиционным портфелем, точно зная доходность каждого инструмента.
                </p>
                <div className="mt-5 space-y-2">
                  {['Каждый канал оцифрован и измерен', 'Бюджет перетекает в прибыльные каналы', 'Решения принимаются на основе данных'].map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <i className="ri-checkbox-circle-line text-[#C5A880] text-base flex-shrink-0"></i>
                      <span className="text-sm text-gray-700 font-['Montserrat']">{t}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
